import { useEffect, useRef, CSSProperties, PointerEvent } from "react";
import classnamesBind from "classnames/bind";
import Image from "../image/Image";
import ClientOnly from "../../containers/clientOnly/ClientOnly";
import { useImageGalleryContext } from "../imageGallery/ImageGalleryContext";
import { useZoomableImageContext } from "./ZoomableImageContext";
import useZoomState from "../../hooks/useZoomState";
import useTap from "../../hooks/useTap";
import usePinchZoom from "../../hooks/usePinchZoom";
import useDragPan from "../../hooks/useDragPan";
import useWheelZoom from "../../hooks/useWheelZoom";
import resetClippedContainerScroll from "../../utils/resetClippedContainerScroll";
import KeyboardZoomControls from "./KeyboardZoomControls";
import styles from "./ZoomableImage.scss";

const classnames = classnamesBind.bind(styles);

const IMAGE_WIDTH_HEIGHT_RATIO = 1.2727;

type ImageType = "hero" | "carousel" | "thumbnail" | "collage";

interface ZoomableImageProps {
  url: string;
  alternateText: string;
  additionalClasses?: string;
  loadingType?: string;
  ariaHidden?: boolean;
  width: number;
  zoomWidth: number;
  sizes?: string;
  zoomScaleLevels?: number[];
  type?: ImageType;
  onLoad?: () => void;
  dataTimingId?: string;
  index?: number;
}

export default function ZoomableImage({
  additionalClasses,
  loadingType,
  url,
  alternateText,
  ariaHidden,
  width,
  zoomWidth,
  sizes,
  zoomScaleLevels = [0.5, 1],
  type = "hero",
  onLoad,
  dataTimingId,
  index = 0,
}: ZoomableImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasDraggedRef = useRef<boolean>(false);
  const isPinchingRef = useRef<boolean>(false);

  const zoomHeight = zoomWidth * IMAGE_WIDTH_HEIGHT_RATIO;
  const { setIsSwipeDisabled, goToNext, goToPrevious } =
    useImageGalleryContext();
  const { recordZoomForImageIndex, getRecordedZoomForImageIndex } =
    useZoomableImageContext();

  const {
    currentScale,
    positionX,
    positionY,
    isZoomed,
    isMaxZoom,
    zoomScaleLevels: effectiveZoomScaleLevels,
    currentScaleRef,
    positionXRef,
    positionYRef,
    handleClickZoom,
    handleSmoothZoom,
    updatePosition,
    loadZoomedContent,
    zoomedContentReady,
    handleZoomedContentReady,
  } = useZoomState({
    containerRef,
    contentWidth: zoomWidth,
    contentHeight: zoomHeight,
    zoomScaleLevels,
    shouldResetZoom: !!ariaHidden,
  });

  const recordedZoom = getRecordedZoomForImageIndex(index);
  const isMirroring = !!ariaHidden && !!recordedZoom;
  const displayZoom = isMirroring
    ? recordedZoom
    : { scale: currentScale, positionX, positionY };
  const isDisplayZoomed = isMirroring ? recordedZoom.scale > 0 : isZoomed;

  useEffect(() => {
    if (ariaHidden) {
      return;
    }

    recordZoomForImageIndex(index, {
      scale: currentScale,
      positionX,
      positionY,
    });
  }, [
    ariaHidden,
    index,
    currentScale,
    positionX,
    positionY,
    recordZoomForImageIndex,
  ]);

  useEffect(() => {
    setIsSwipeDisabled(isZoomed);
  }, [isZoomed, setIsSwipeDisabled]);

  const { onClick, onTouchEnd, onDoubleClick } = useTap({
    onTap: handleClickZoom,
    hasDraggedRef,
    isPinchingRef,
    mouseTapMode: "single",
  });

  const pinch = usePinchZoom({
    containerRef,
    onPinch: handleSmoothZoom,
    isPinchingRef,
  });

  const drag = useDragPan({
    containerRef,
    isEnabled: isZoomed,
    currentScaleRef,
    positionXRef,
    positionYRef,
    contentWidth: zoomWidth,
    contentHeight: zoomHeight,
    onPan: updatePosition,
    hasDraggedRef,
    onZoomedEdgeSwipeNavigate: (direction) => {
      direction === "next" ? goToNext() : goToPrevious();
    },
  });

  useWheelZoom({
    containerRef,
    onZoom: handleSmoothZoom,
  });

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    const pinchConsumed = pinch.onPointerDown(event);
    if (!pinchConsumed) {
      drag.onPointerDown(event);
    }
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const pinchConsumed = pinch.onPointerMove(event);
    if (!pinchConsumed) {
      drag.onPointerMove(event);
    }
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>): void => {
    pinch.onPointerUp(event);
    drag.onPointerUp(event);
  };

  const handlePointerCancel = (event: PointerEvent<HTMLDivElement>): void => {
    pinch.onPointerCancel(event);
  };

  const zoomedImageStyle: CSSProperties = {
    width: `${zoomWidth}px`,
    height: `${zoomHeight}px`,
    maxWidth: "none",
    maxHeight: "none",
    display: isDisplayZoomed && zoomedContentReady ? "block" : "none",
    transform: `scale(${displayZoom.scale}) translate(-${displayZoom.positionX}%, -${displayZoom.positionY}%)`,
  };

  return (
    <div
      ref={containerRef}
      className={classnames(styles.container, {
        zoomed: isZoomed,
        maxZoom: isMaxZoom,
      })}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onTouchEnd={onTouchEnd}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onScroll={resetClippedContainerScroll}
    >
      <Image
        type={type}
        additionalClasses={additionalClasses}
        url={url}
        width={width}
        alternateText={alternateText}
        loadingType={loadingType}
        ariaHidden={ariaHidden}
        sizes={sizes}
        draggable={false}
        dataTestId="zoomableImage"
        onLoad={onLoad}
        dataTimingId={dataTimingId}
      />
      {(loadZoomedContent || isDisplayZoomed) && (
        <Image
          type={type}
          additionalClasses={styles.preloadedImage}
          url={url}
          width={zoomWidth}
          alternateText={alternateText}
          loadingType="eager"
          ariaHidden={true}
          onLoad={handleZoomedContentReady}
          style={zoomedImageStyle}
          draggable={false}
          dataTestId="zoomedImage"
        />
      )}
      <ClientOnly>
        <KeyboardZoomControls
          containerRef={containerRef}
          isZoomed={isZoomed}
          currentScale={currentScale}
          currentScaleRef={currentScaleRef}
          positionXRef={positionXRef}
          positionYRef={positionYRef}
          contentWidth={zoomWidth}
          contentHeight={zoomHeight}
          zoomScaleLevels={effectiveZoomScaleLevels}
          isInactive={Boolean(ariaHidden)}
          onClickZoom={handleClickZoom}
          onPan={updatePosition}
        />
      </ClientOnly>
    </div>
  );
}
